import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Phone, Mail, MapPin, Clock, Send, User, MessageSquare, 
  Truck, Shield, Award, CheckCircle, ArrowRight, Star
} from 'lucide-react';
import { contactInfo, companyInfo, heroImages } from '../data/mock';
import lepusTruck from '../assets/images/lepus-group-truck.jpeg';
import emailjs from '@emailjs/browser'; // ✅ added import

// Initialize EmailJS with your public key
emailjs.init("r18TvQWaOZW9ctJHU"); // ✅ replace with your actual public key

// Clean animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const ContactCard = ({ contact, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const iconMap = {
    Phone: Phone,
    Mail: Mail,
    MapPin: MapPin,
    Clock: Clock
  };

  const IconComponent = iconMap[contact.icon] || Phone;

  return (
    <motion.div
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#1d526d]/20 h-full flex flex-col group hover:-translate-y-1">
        <motion.div
          className="w-16 h-16 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-xl flex items-center justify-center mb-6 shadow-lg"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <IconComponent className="w-8 h-8 text-white" />
        </motion.div>
        
        <h3 className="text-2xl font-bold text-black mb-4 group-hover:text-[#1d526d] transition-colors duration-300">
          {contact.title}
        </h3>
        
        <div className="space-y-3 mb-6 flex-grow">
          {contact.phone && (
            <motion.a
              href={`tel:${contact.phoneRaw || contact.phone}`}
              className="flex items-center text-gray-600 hover:text-[#1d526d] transition-colors font-medium"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.3 }}
            >
              <Phone className="w-4 h-4 mr-3 text-[#1d526d] flex-shrink-0" />
              <span>{contact.phone}</span>
            </motion.a>
          )}
          
          {contact.email && (
            <motion.a
              href={`mailto:${contact.email}`}
              className="flex items-center text-gray-600 hover:text-[#1d526d] transition-colors font-medium"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.3 }}
            >
              <Mail className="w-4 h-4 mr-3 text-[#1d526d] flex-shrink-0" />
              <span>{contact.email}</span>
            </motion.a>
          )}
          
          {contact.address && (
            <div className="flex items-start text-gray-600 font-medium">
              <MapPin className="w-4 h-4 mr-3 text-[#1d526d] mt-1 flex-shrink-0" />
              <div>
                <div>{contact.address}</div>
                <div>{contact.city}</div>
              </div>
            </div>
          )}
          
          {contact.hours && (
            <div className="flex items-start text-gray-600 font-medium">
              <Clock className="w-4 h-4 mr-3 text-[#1d526d] mt-1 flex-shrink-0" />
              <div>{contact.hours}</div>
            </div>
          )}
        </div>
        
        {contact.description && (
          <p className="text-gray-500 leading-relaxed mb-4">
            {contact.description}
          </p>
        )}
        
        <motion.div
          className="flex items-center text-[#1d526d] font-bold mt-auto"
          whileHover={{ x: 4 }}
          transition={{ duration: 0.3 }}
        >
          <span>Contact Now</span>
          <ArrowRight className="w-4 h-4 ml-2" />
        </motion.div>
      </div>
    </motion.div>
  );
};

const Contact = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // ✅ Send email using EmailJS
      const result = await emailjs.send(
        'service_da6wpf1',    // replace with your EmailJS service ID
        'template_e7hyegh',   // replace with your EmailJS template ID
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          subject: formData.subject,
          message: formData.message,
          to_email: 'info@lepus.ca',
          submission_time: new Date().toLocaleString()
        }
      );

      console.log('Email sent successfully:', result);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // (rest of your code remains unchanged: contactMethods, features, JSX...)

  const contactMethods = [
    {
      title: contactInfo.headquarters.title,
      phone: contactInfo.headquarters.phone,
      phoneRaw: contactInfo.headquarters.phoneRaw,
      email: contactInfo.headquarters.email,
      address: contactInfo.headquarters.address,
      city: contactInfo.headquarters.city,
      hours: contactInfo.headquarters.hours,
      icon: 'MapPin'
    },
    {
      title: contactInfo.operations.title,
      phone: contactInfo.operations.phone,
      phoneRaw: contactInfo.operations.phoneRaw,
      email: contactInfo.operations.email,
      hours: contactInfo.operations.hours,
      description: "24/7 emergency support and dispatch services",
      icon: 'Clock'
    },
    {
      title: contactInfo.newOrders.title,
      phone: contactInfo.newOrders.phone,
      phoneRaw: contactInfo.newOrders.phoneRaw,
      email: contactInfo.newOrders.email,
      description: contactInfo.newOrders.description,
      icon: 'Phone'
    },
    {
      title: contactInfo.careers.title,
      phone: contactInfo.careers.phone,
      phoneRaw: contactInfo.careers.phoneRaw,
      email: contactInfo.careers.email,
      description: contactInfo.careers.description,
      icon: 'Mail'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Secure Communication",
      description: "Your information is protected with industry-standard security"
    },
    {
      icon: Award,
      title: "Expert Support",
      description: "Knowledgeable team ready to assist with all your needs"
    },
    {
      icon: CheckCircle,
      title: "Quick Response",
      description: "We respond to all inquiries within 24 hours"
    },
    {
      icon: Star,
      title: "Personalized Service",
      description: "Tailored solutions for your specific requirements"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Clean & Modern */}
      <section className="relative min-h-screen bg-gradient-to-br from-gray-50 to-white overflow-hidden pt-4 sm:pt-6 lg:pt-8">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src={lepusTruck}
            alt="Contact Lepus Group" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/95"></div>
        </div>

        {/* Clean Geometric Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#1d526d]/5 to-transparent rounded-full blur-2xl"
          />
        </div>

        <div ref={heroRef} className="relative z-10 min-h-screen flex items-center py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <div className="text-center">
              <motion.h1
                variants={slideIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-5xl lg:text-6xl font-bold text-black mb-8"
              >
                Contact
                <span className="block text-[#1d526d] mt-2">Us</span>
              </motion.h1>
              
              <motion.p
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
              >
                Ready to experience transportation excellence? Get in touch with our team for personalized service and expert solutions.
              </motion.p>

              <motion.div
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="flex flex-col sm:flex-row gap-6 justify-center"
              >
                <motion.a 
                  href="#contact-form"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#1d526d] hover:bg-black text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl inline-flex items-center justify-center group"
                >
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </motion.a>
                <motion.a 
                  href={`tel:${companyInfo.phoneRaw}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-[#1d526d] text-[#1d526d] px-10 py-4 rounded-xl font-bold text-lg hover:bg-[#1d526d] hover:text-white transition-all duration-300 inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-3" />
                  Call Now
                </motion.a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Features */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Why Contact Us?</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Experience the difference with our professional, responsive, and personalized approach to customer service.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="text-center bg-white rounded-2xl px-4 py-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <motion.div
                  className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-[#1d526d] to-[#2a6b89] rounded-2xl flex items-center justify-center mx-auto mb-6"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <feature.icon className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                </motion.div>
                <h3 className="text-lg lg:text-l font-bold text-black mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Get in Touch</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Multiple ways to reach us for all your transportation and logistics needs.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {contactMethods.map((contact, index) => (
              <ContactCard key={index} contact={contact} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, y: 30 }}
            animate={formInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Send us a Message</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Ready to get started? Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={formInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl p-8 lg:p-12"
          >
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <p className="text-green-700 font-bold">Message sent successfully!</p>
                </div>
                <p className="text-green-600 mt-1">We'll get back to you within 24 hours.</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block font-bold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1d526d] focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block font-bold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1d526d] focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block font-bold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1d526d] focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
  <label htmlFor="subject" className="block font-bold text-gray-700 mb-2">
    Subject *
  </label>
  <div className="relative">
    <Truck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
    <select
      id="subject"
      name="subject"
      value={formData.subject}
      onChange={handleChange}
      required
      disabled={isSubmitting}
      className="w-full appearance-none pl-12 pr-12 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1d526d] focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <option value="">Select a subject</option>
      <option value="Request Quote">Request Quote</option>
      <option value="General Inquiry">General Inquiry</option>
      <option value="Customer Support">Customer Support</option>
      <option value="Careers">Careers</option>
    </select>
    {/* Dropdown arrow */}
    <svg
      className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </div>
</div>

              </div>

              <div>
                <label htmlFor="message" className="block font-bold text-gray-700 mb-2">
                  Message *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-4 text-gray-400 w-5 h-5" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows={5}
                    className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1d526d] focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Tell us about your transportation needs..."
                  />
                </div>
              </div>

              <div className="text-center">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={isSubmitting ? {} : { scale: 1.05 }}
                  whileTap={isSubmitting ? {} : { scale: 0.95 }}
                  className="bg-[#1d526d] hover:bg-black text-white px-12 py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center mx-auto group shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-3 group-hover:translate-x-1 transition-transform" />
                      Send Message
                    </>
                  )}
                </motion.button>
                <p className="text-gray-500 mt-4">
                  We'll respond within 24 hours. Fields marked with * are required.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#1d526d] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={lepusTruck}
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Ship?</h2>
            <p className="text-xl text-gray-200 mb-12 leading-relaxed max-w-4xl mx-auto">
              Don't wait - get your transportation solution started today. Our team is standing by to help you move forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.a 
                href={`tel:${companyInfo.phoneRaw}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#1d526d] px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center justify-center group shadow-2xl"
              >
                <Phone className="w-5 h-5 mr-3" />
                Call {companyInfo.phone}
              </motion.a>
              <motion.a 
                href={`mailto:${companyInfo.email}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-[#1d526d] transition-all duration-300 flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-3" />
                Email Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;